import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { lastValueFrom, Observable, of, throwError as observableThrowError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiHelperService } from './api-helper.service';
import { GenericAPIResponse } from '../models/api-response';
import { FilmMediaCapabilities, FilmSession, FilmSessionJoin, FilmSessionRecording, StartFilmSessionRequest } from '../models/film-session';
import { Team } from '../models/team';
import { User } from '../models/user';

/**
 * REST client for live film sessions. The API answers refusals with an HTTP error status and a
 * `{success: false, message, result?}` body; those resolve as that body so callers can show the message
 * (and, for a team that already has a live session, join the session in `result`).
 */
@Injectable({
  providedIn: 'root'
})
export class FilmSessionService {

  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelperService
  ) { }

  private url(path: string = ''): string {
    return `${this.apiHelper.apiUrl()}/film-sessions${path}`;
  }

  private send<T>(request: Observable<GenericAPIResponse<T>>): Promise<GenericAPIResponse<T>> {
    return lastValueFrom(request.pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.error?.success === false) return of(err.error as GenericAPIResponse<T>);
        console.log(err);
        return observableThrowError(err.message);
      })
    ));
  }

  start(request: StartFilmSessionRequest): Promise<GenericAPIResponse<FilmSession>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<FilmSession>>(this.url(), request, { headers }));
  }

  /** The team's live session if the caller is invited to it, otherwise a null result. */
  getLive(teamid: Team['_id']): Promise<GenericAPIResponse<FilmSession | null>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    const params = new HttpParams().set('teamid', teamid);
    return this.send(this.http.get<GenericAPIResponse<FilmSession | null>>(this.url('/live'), { headers, params }));
  }

  join(sessionId: FilmSession['_id']): Promise<GenericAPIResponse<FilmSessionJoin>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<FilmSessionJoin>>(this.url(`/${sessionId}/join`), {}, { headers }));
  }

  leave(sessionId: FilmSession['_id']): Promise<GenericAPIResponse<{left: boolean}>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<{left: boolean}>>(this.url(`/${sessionId}/leave`), {}, { headers }));
  }

  end(sessionId: FilmSession['_id']): Promise<GenericAPIResponse<FilmSession>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<FilmSession>>(this.url(`/${sessionId}/end`), {}, { headers }));
  }

  remove(sessionId: FilmSession['_id'], userid: User['_id']): Promise<GenericAPIResponse<{removed: boolean}>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<{removed: boolean}>>(this.url(`/${sessionId}/remove/${userid}`), {}, { headers }));
  }

  /** A coach lets a member speak (call on) or mutes them, or allows or stops an athlete's camera. */
  setMedia(sessionId: FilmSession['_id'], userid: User['_id'], grant: {speak?: boolean; camera?: boolean}): Promise<GenericAPIResponse<FilmMediaCapabilities>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<FilmMediaCapabilities>>(this.url(`/${sessionId}/participants/${userid}/media`), grant, { headers }));
  }

  /** Saves a finished session's drawings to the clips, shared with the whole team. Saves once. */
  saveDrawings(sessionId: FilmSession['_id']): Promise<GenericAPIResponse<{saved: number; alreadySaved?: boolean}>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<{saved: number; alreadySaved?: boolean}>>(this.url(`/${sessionId}/drawings/save`), {}, { headers }));
  }

  /** Opens the floor to every athlete and parent, or closes it, which also mutes everyone a coach called on. */
  setFloor(sessionId: FilmSession['_id'], open: boolean): Promise<GenericAPIResponse<{floorOpen: boolean}>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<{floorOpen: boolean}>>(this.url(`/${sessionId}/floor`), { open }, { headers }));
  }

  /** Starts recording the live session from what's on screen now. A session records once. */
  startRecording(sessionId: FilmSession['_id']): Promise<GenericAPIResponse<FilmSessionRecording>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<FilmSessionRecording>>(this.url(`/${sessionId}/recording/start`), {}, { headers }));
  }

  stopRecording(sessionId: FilmSession['_id']): Promise<GenericAPIResponse<FilmSessionRecording>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<FilmSessionRecording>>(this.url(`/${sessionId}/recording/stop`), {}, { headers }));
  }

  private recordingUrl(recordingId: FilmSessionRecording['_id'], path: string = ''): string {
    return `${this.apiHelper.apiUrl()}/film-session-recordings/${recordingId}${path}`;
  }

  /** A recording, with a signed link to its video once it's ready. */
  getRecording(recordingId: FilmSessionRecording['_id']): Promise<GenericAPIResponse<FilmSessionRecording>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.get<GenericAPIResponse<FilmSessionRecording>>(this.recordingUrl(recordingId), { headers }));
  }

  /** Moves a recording to Recently deleted, where it's kept 30 days. */
  deleteRecording(recordingId: FilmSessionRecording['_id']): Promise<GenericAPIResponse<FilmSessionRecording>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.delete<GenericAPIResponse<FilmSessionRecording>>(this.recordingUrl(recordingId), { headers }));
  }

  /** Brings a recording back from Recently deleted. */
  restoreRecording(recordingId: FilmSessionRecording['_id']): Promise<GenericAPIResponse<FilmSessionRecording>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<FilmSessionRecording>>(this.recordingUrl(recordingId, '/restore'), {}, { headers }));
  }

  /** A short-lived link that saves the video as a file (coaches only). */
  getRecordingDownload(recordingId: FilmSessionRecording['_id']): Promise<GenericAPIResponse<{url: string; fileName: string}>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.get<GenericAPIResponse<{url: string; fileName: string}>>(this.recordingUrl(recordingId, '/download'), { headers }));
  }

  /** Processes a recording that failed again. */
  retryRecording(recordingId: FilmSessionRecording['_id']): Promise<GenericAPIResponse<FilmSessionRecording>> {
    const headers = this.apiHelper.apiHeaderWithToken();
    return this.send(this.http.post<GenericAPIResponse<FilmSessionRecording>>(this.recordingUrl(recordingId, '/retry'), {}, { headers }));
  }
}
